import { IconChevronDown, IconLogout } from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'

import type { UserDetailsResponse } from '@/utils/api/user-service/models'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { useAuth } from '@/features/authentication/components/auth-context.tsx'
import { getUserInitials } from '@/lib/user-initials.ts'
import { getUserRoleDisplay } from '@/lib/user-role-display.tsx'

import { Breadcrumbs } from './breadcrumbs'

export function SiteHeader() {
  const { logout, user } = useAuth()
  const navigate = useNavigate()

  async function handleLogout() {
    try {
      // Mark the navigation as an intentional logout to influence guards
      window.sessionStorage.setItem('justLoggedOut', '1')
    }
    catch {}
    logout()
    await navigate({ to: '/login', search: {}, replace: true } as any)
  }

  return (
    <header
      className="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height) backdrop-blur-sm"
    >
      <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
        <SidebarTrigger className="-ml-1" />
        <Separator
          orientation="vertical"
          className="mx-2 data-[orientation=vertical]:h-4"
        />
        <Breadcrumbs />
        <div className="ml-auto flex items-center gap-3">
          <UserMenu
            user={user}
            onLogout={handleLogout}
          />
        </div>
      </div>
    </header>
  )
}

interface UserMenuProps {
  user: UserDetailsResponse | null
  onLogout: () => Promise<void>
}

function UserMenu({
  user,
  onLogout,
}: UserMenuProps) {
  if (!user) {
    return null
  }

  const primaryName = user.name?.trim() || `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
  const displayName = primaryName || user.username || 'Account'
  const roleDisplay = getUserRoleDisplay(user, 'Member')
  const initials = getUserInitials(user)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="group flex items-center gap-3 bg-background/70 px-2.5 py-1.5 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <span className="relative inline-flex">
            <Avatar className="h-9 w-9 ring-2 ring-primary/10 shadow">
              <AvatarImage src={user.profilePicUrl ?? ''} alt={displayName} />
              <AvatarFallback className="bg-primary/10 text-sm font-semibold text-primary">
                {initials}
              </AvatarFallback>
            </Avatar>
          </span>
          <span className="hidden sm:flex flex-col items-start leading-tight">
            <span className="text-sm font-semibold text-foreground">{displayName}</span>
            <span className="text-xs text-muted-foreground">{roleDisplay}</span>
          </span>
          <IconChevronDown className="size-4 text-muted-foreground transition group-hover:text-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={10}
        className="min-w-64 rounded-xl border bg-card/95 p-2 shadow-xl backdrop-blur"
      >
        <DropdownMenuLabel className="flex items-center gap-3 px-2 py-1.5">
          <Avatar className="h-10 w-10 ring-2 ring-primary/10 shadow">
            <AvatarImage src={user.profilePicUrl ?? ''} alt={displayName} />
            <AvatarFallback className="bg-primary/10 text-sm font-semibold text-primary">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="space-y-0.5">
            <p className="text-sm font-semibold leading-tight">{displayName}</p>
            {user.email ? <p className="text-xs text-muted-foreground">{user.email}</p> : null}
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/80">{roleDisplay}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => void onLogout()}
          className="gap-2 py-2 text-destructive focus:text-destructive"
        >
          <IconLogout className="size-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
